import { branding } from '@guridosdrones/shared';
import { ArrowRight } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components';

const highlightedCards = [
  {
    title: 'Curso Completo de Piloto Agrícola',
    description:
      'Do zero à operação com confiança. Domine pilotagem, legislação e tecnologia de aplicação para atuar no mercado.',
    image: '/assets/images/msn_01.webp',
    badge: 'Missão Principal',
    code: 'MSN_01',
    emphasis: 'primary',
  },
  {
    title: 'Mapeamento Aéreo 3D com Relevo',
    description:
      'Crie mapas de precisão que respeitam a topografia real e otimize cada missão de aplicação.',
    image: '/assets/images/msn_02.webp',
    badge: 'Especialização',
    code: 'MSN_02',
    emphasis: 'regular',
  },
  {
    title: 'Manutenção de Drones Agrícolas',
    description:
      'Diagnóstico, prevenção e rotina técnica para manter sua frota operando com segurança e lucratividade.',
    image: '/assets/images/msn_03.webp',
    badge: 'Performance',
    code: 'MSN_03',
    emphasis: 'regular',
  },
] as const;

export function HighlightedPaths() {
  return (
    <section className="section-shell bg-background">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--tw-gradient-stops))] from-primary/8 via-background to-background" />

      <div className="section-container">
        <FadeIn direction="up" distance={24} className="section-header">
          <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
            Expansão de Conhecimento
          </p>
          <h2 className="section-title">Vá Além da Pilotagem Básica</h2>
          <p className="section-description">
            Domine cada etapa da operação com drones de pulverização do voo ao mapa, da manutenção
            ao resultado no campo.
          </p>
        </FadeIn>

        <StaggerContainer
          className="mobile-snap-row md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 lg:grid-cols-3"
          staggerDelay={0.1}
        >
          {highlightedCards.map((card) => {
            const isPrimary = card.emphasis === 'primary';

            return (
              <StaggerItem
                key={card.title}
                className="mobile-snap-item h-full min-w-[84vw] sm:min-w-[72vw] md:min-w-0"
              >
                <article
                  className={`group relative overflow-hidden border transition-all duration-500 ${
                    isPrimary
                      ? 'h-full min-h-96 border-primary/40 shadow-[0_24px_80px_-42px_rgba(16,185,129,0.5)] lg:min-h-120'
                      : 'h-full min-h-96 border-border/50 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.4)] lg:min-h-120'
                  }`}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />

                  <div
                    className={`absolute inset-0 ${
                      isPrimary
                        ? 'bg-linear-to-t from-black/90 via-black/45 to-black/15'
                        : 'bg-linear-to-t from-black/88 via-black/42 to-black/20'
                    }`}
                  />

                  <div className="relative z-10 flex h-full flex-col justify-between p-6 sm:p-8">
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center gap-2 border border-white/20 bg-black/40 px-3 py-1.5 backdrop-blur-md">
                        <span className="relative flex h-1.5 w-1.5">
                          <span
                            className={`absolute inline-flex h-full w-full rounded-full ${isPrimary ? 'animate-ping bg-primary opacity-75' : 'bg-white/60'}`}
                          />
                          <span
                            className={`relative inline-flex h-1.5 w-1.5 rounded-full ${isPrimary ? 'bg-primary' : 'bg-white/60'}`}
                          />
                        </span>
                        <span className="font-mono text-[10px] font-bold tracking-[0.15em] text-white/80 uppercase">
                          {card.badge}
                        </span>
                      </div>
                      <span className="font-mono text-[10px] tracking-widest text-white/30">
                        {card.code}
                      </span>
                    </div>

                    <div>
                      <h3
                        className={`text-balance font-heading font-bold leading-tight text-white ${
                          isPrimary ? 'text-2xl sm:text-[2rem]' : 'text-2xl'
                        }`}
                      >
                        {card.title}
                      </h3>
                      <p className="mt-4 max-w-[36ch] text-sm leading-relaxed text-white/70 sm:text-base">
                        {card.description}
                      </p>

                      <button
                        type="button"
                        className={`group/btn mt-7 inline-flex h-11 items-center gap-2 px-5 font-mono text-xs font-bold tracking-widest uppercase transition-all ${
                          isPrimary
                            ? 'border border-primary/40 bg-primary/15 text-primary hover:border-primary/70 hover:bg-primary/25'
                            : 'border border-white/20 bg-white/8 text-white/80 backdrop-blur-sm hover:border-white/40 hover:bg-white/14'
                        }`}
                        onClick={() => window.open(branding.contact.whatsapp, '_blank')}
                      >
                        Quero Saber Mais
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" />
                      </button>
                    </div>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
}
